import React, { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { Button, Form, Input, Modal, Select, Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { entityList } from '../entity-list'
import { AddModalProps } from '../interfaces'
import { UploadFileStatus, UploadFile } from 'antd/es/upload/interface'
import TemplateEditor from '../../message-templates-editor'

const AddModal: React.FC<AddModalProps> = memo((props: AddModalProps) => {
  const { officesList, onOk, onCancel, record, modalIsOpen } = props
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [isOkDisabled, setIsOkDisabled] = useState(true)
  const [templateType, setTemplateType] = useState(record?.type || 'doc')
  const [officeId, setOfficeId] = useState<number | string | undefined>(
    record?.office_id ? Number(record.office_id) : undefined,
  )
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isInitializingRef = useRef(false)
  const formInitializedRef = useRef(false)
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const recordValues = useMemo(() => {
    if (!record) return null
    return {
      id: record.id,
      type: record.type,
      office_id: record.office_id,
      name: record.name,
      entity: record.entity,
      description: record.description,
      message: record.message,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    record?.id,
    record?.type,
    record?.office_id,
    record?.name,
    record?.entity,
    record?.description,
    record?.message,
  ])

  useEffect(() => {
    if (modalIsOpen) {
      isInitializingRef.current = true
      formInitializedRef.current = true

      const values = recordValues
        ? {
            type: recordValues.type || 'doc',
            office_id: recordValues.office_id,
            name: recordValues.name || '',
            entity: recordValues.entity,
            description: recordValues.description || '',
            message: recordValues.message || undefined,
          }
        : {
            type: 'doc',
            office_id: undefined,
            name: '',
            entity: undefined,
            description: '',
            message: undefined,
          }

      form.resetFields()
      form.setFieldsValue(values)
      setTemplateType(recordValues?.type || 'doc')
      const officeIdValue = recordValues?.office_id || values.office_id
      setOfficeId(officeIdValue ? Number(officeIdValue) : undefined)

      if (recordValues && recordValues.type !== 'message' && record?.file_name) {
        setFileList([
          {
            uid: `file_${record.id}`,
            name: record.file_name,
            status: 'done' as UploadFileStatus,
            url: `/api/template/${record.id}`,
          },
        ])
      } else {
        setFileList([])
      }

      requestAnimationFrame(async () => {
        try {
          await form.validateFields()
          setIsOkDisabled(false)
        } catch (error) {
          setIsOkDisabled(true)
        } finally {
          setTimeout(() => {
            isInitializingRef.current = false
          }, 100)
        }
      })
    } else {
      if (formInitializedRef.current) {
        form.resetFields()
      }
      setTemplateType('doc')
      setOfficeId(undefined)
      setFileList([])
      setIsOkDisabled(true)
      formInitializedRef.current = false
      isInitializingRef.current = false

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
        validationTimeoutRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen, recordValues])

  const handleOk = useCallback(async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()
      setLoading(true)
      const formData = new FormData()

      if (['doc', 'xls', 'pdf'].includes(templateType)) {
        const file = values?.file as File | undefined
        if (file instanceof File) {
          formData.append('file', file)
        } else {
          console.warn('File is not a File instance:', file, typeof file)
        }
      }

      formData.append('name', values?.name)
      formData.append('entity', values?.entity)
      formData.append('type', values?.type)

      if (values?.description) {
        formData.append('description', values.description)
      }

      if (values?.office_id) {
        formData.append('office_id', values.office_id)
      }

      if (templateType === 'message' && values?.message) {
        formData.append('message', values.message)
      }

      try {
        const url = record?.id ? `/api/template/${record.id}` : '/api/add-template'
        const rawResponse = await fetch(url, {
          method: 'POST',
          body: formData,
        })
        const response = await rawResponse.json().catch(() => null)
        // Раньше ошибка сервера (500) закрывала модалку как успех — шаблон «добавлялся», но не появлялся
        if (!rawResponse.ok || !response?.id) {
          const detail =
            response?.errors?.[0]?.detail ||
            (typeof response?.errors?.[0] === 'string' ? response.errors[0] : null) ||
            'Не удалось сохранить шаблон'
          message.error(detail)
          return
        }
        onOk?.(response)
      } catch (error) {
        console.warn(error)
        return { error }
      } finally {
        setLoading(false)
      }
    } catch (errInfo) {
      console.error('Validate Failed:', errInfo)
    }
  }, [form, templateType, record, onOk])

  const selectOptions = useMemo(
    () =>
      Object.keys(entityList).map(key => {
        const params = entityList[key]
        return {
          value: key,
          label: params?.label,
          disabled: params?.disabled,
        }
      }),
    [],
  )

  const typeOptions = useMemo(
    () => [
      { value: 'doc', label: 'Документ DOC' },
      { value: 'xls', label: 'Документ XLS' },
      { value: 'pdf', label: 'Документ PDF' },
      { value: 'message', label: 'Сообщение' },
    ],
    [],
  )

  const officeOptions = useMemo(
    () => (templateType != 'message' ? officesList?.sale || [] : officesList?.hr || []),
    [templateType, officesList],
  )
  const formItemStyle = { marginBottom: 16 }

  const handleTypeChange = useCallback(
    (value: string) => {
      setTemplateType(value)
      setOfficeId(undefined)
      form.resetFields(['office_id', 'file', 'message'])
    },
    [form],
  )

  const handleFileChange = useCallback(
    (info: any) => {
      const { fileList: newFileList } = info
      setFileList(newFileList)

      const { file } = info
      const fileObj = file.originFileObj || (file instanceof File ? file : null)
      if (fileObj instanceof File) {
        form.setFieldsValue({
          file: fileObj,
        })
        form.validateFields(['file']).catch(() => undefined)
      }
    },
    [form],
  )

  const handleFileRemove = useCallback(() => {
    setFileList([])
    form.setFieldsValue({ file: undefined })
    form.validateFields(['file']).catch(() => undefined)
    return true
  }, [form])

  const variablesHr = [
    { key: 'candidate_name', label: 'ФИО кандидата' },
    { key: 'manager_name', label: 'ФИО менеджера' },
    { key: 'vacancy', label: 'Вакансия' },
    { key: 'interview_type', label: 'Тип собеседования' },
    { key: 'employment_date', label: 'Дата выхода на работу' },
    { key: 'interview_datetime', label: 'Дата и время собеседования' },
    { key: 'candidate_phone', label: 'Телефон кандидата' },
    { key: 'manager_phone', label: 'Телефон менеджера' },
  ]

  const accept = useMemo(() => {
    if (templateType === 'doc') return '.doc, .docx'
    if (templateType === 'xls') return '.xls, .xlsx'
    if (templateType === 'pdf') return '.pdf'
  }, [templateType])

  const onFieldsChange = useCallback(() => {
    if (isInitializingRef.current) {
      return
    }
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    validationTimeoutRef.current = setTimeout(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setIsOkDisabled(false))
        .catch(() => setIsOkDisabled(true))
    }, 150)
  }, [form])

  return (
    <Modal
      key={record?.id || 'new'}
      title={!record?.id ? 'Загрузка нового шаблона' : 'Изменение шаблона'}
      onOk={handleOk}
      onCancel={onCancel}
      open={modalIsOpen}
      destroyOnHidden={true}
      okButtonProps={{ disabled: isOkDisabled || loading }}
    >
      {modalIsOpen && (
        <Form
          form={form}
          labelCol={{ flex: '120px' }}
          labelAlign="left"
          labelWrap
          wrapperCol={{ flex: 1 }}
          onFieldsChange={onFieldsChange}
        >
          <Form.Item
            label="Тип шаблона"
            name="type"
            style={formItemStyle}
            rules={[{ required: true, message: 'Выберите Тип шаблона!' }]}
          >
            <Select
              placeholder="Выберите тип"
              options={typeOptions}
              onChange={handleTypeChange}
              disabled={loading}
            />
          </Form.Item>
          {officeOptions?.length ? (
            <Form.Item
              label="Офис"
              name="office_id"
              style={formItemStyle}
              rules={[{ required: true, message: 'Выберите Офис!' }]}
            >
              <Select
                placeholder="Выберите Офис"
                options={officeOptions}
                disabled={loading}
                onChange={value => setOfficeId(value)}
              />
            </Form.Item>
          ) : null}
          {officeId ? (
            <Form.Item
              label="Расположение"
              name="entity"
              style={formItemStyle}
              dependencies={['office_id']}
              rules={[
                ({ getFieldValue }) => ({
                  validator() {
                    if (
                      templateType !== 'message' &&
                      getFieldValue('office_id') &&
                      !getFieldValue('entity')
                    ) {
                      return Promise.reject('Выберите расположение!')
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Select
                placeholder="Выберите расположение"
                options={selectOptions}
                disabled={loading}
              />
            </Form.Item>
          ) : null}
          {templateType != 'message' ? (
            <Form.Item
              label="Файл"
              name="file"
              style={formItemStyle}
              rules={!record?.id ? [{ required: true, message: 'Выберите файл!' }] : []}
            >
              <div style={{ maxWidth: '350px' }}>
                <Upload
                  name="file"
                  accept={accept}
                  beforeUpload={() => false}
                  showUploadList={true}
                  fileList={fileList}
                  disabled={loading}
                  onChange={handleFileChange}
                  onRemove={handleFileRemove}
                >
                  <Button icon={<UploadOutlined />}>Выбрать {templateType.toUpperCase()}</Button>
                </Upload>
              </div>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                key="message-editor"
                label="Сообщение"
                name="message"
                style={formItemStyle}
                rules={[{ required: true, message: 'Заполните сообщение!' }]}
              >
                <TemplateEditor variables={variablesHr} />
              </Form.Item>
            </>
          )}
          <Form.Item
            label="Название"
            name="name"
            style={formItemStyle}
            rules={[{ required: true, message: 'Укажите название!' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item label="Описание" name="description" style={formItemStyle}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 5 }} disabled={loading} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
})

AddModal.displayName = 'AddModal'

export default AddModal
